import moment from "moment-timezone";

import {SAFELISTING_COVERAGE_FORMATS as F} from "@/src/modules/core/lib/formatters";
import type {
  Property,
  PropertyRuleTimeRange,
  PropertyType,
} from "@/src/modules/properties/types";

import {getCoverageAnchorHumanLabel} from "./coverage-anchor-hints";
import {getSafelistingCoverageWindowKind} from "./compute-safelisting-coverage-preview";
import {getSafelistingRangeHoursFromProperty} from "./get-safelisting-range-hours-from-property";
import type {Safelisting, SafelistingDay} from "../types";

/** Days that still grant (or granted) coverage — excludes cancelled/skipped permit nights. */
export function getSafelistingEffectiveCoverageDays(
  days: Safelisting["days"] | undefined,
): SafelistingDay[] {
  return (days ?? []).filter(
    (day) => day.status !== "cancelled" && day.status !== "skipped",
  );
}

/** Night vs date label from property safelisting hours (not from persisted day spans). */
export function getSafelistingCoverageDateWord(
  property: Property | null | undefined,
  propertyTypes: PropertyType[] = [],
): "night" | "date" {
  const range = getSafelistingRangeHoursFromProperty(property, propertyTypes);
  if (!range) return "date";
  return getSafelistingCoverageWindowKind(range) === "overnight" ? "night" : "date";
}

export type SafelistingCoverageDayRow = {
  anchorDate: string;
  status: SafelistingDay["status"];
  startLabel: string;
  endLabel: string;
  compactDateLabel: string;
  compactTimeLabel: string;
  timePatternLabel: string;
  /** Human label relative to now (Tonight/Today/Tomorrow night/Tomorrow), else null. */
  humanLabel: string | null;
};

/** Coverage derived from persisted `days[]` (list/detail API), not property rules. */
export type SafelistingCoverageFromDays = {
  dateWord: "night" | "date";
  /** Count of days that still grant (or granted) coverage. */
  permitCount: number;
  countLabel: string;
  cancelledCount: number;
  skippedCount: number;
  overallRangeLabel: string;
  overallCompactLabel: string;
  sharedTimePattern: string | null;
  windowHint: string;
  /** Human label for the first effective covered date (Tonight/Tomorrow night…), else null. */
  startHumanLabel: string | null;
  rows: SafelistingCoverageDayRow[];
};

function buildOverallCompactLabel(
  rows: SafelistingCoverageDayRow[],
  dateWord: "night" | "date",
): string {
  const first = rows[0]!;
  const last = rows[rows.length - 1]!;

  if (rows.length === 1) {
    return `${first.compactDateLabel}, ${first.compactTimeLabel}`;
  }

  if (dateWord === "night") {
    const startTime = first.compactTimeLabel.split(" → ")[0] ?? first.compactTimeLabel;
    const endTime = last.compactTimeLabel.split(" → ").pop() ?? last.compactTimeLabel;
    return `${first.compactDateLabel} – ${last.compactDateLabel}, ${startTime} → ${endTime}`;
  }

  return `${first.compactDateLabel} – ${last.compactDateLabel}`;
}

function mapDayToCoverageRow(
  day: SafelistingDay,
  tz: string,
  rangeHours?: PropertyRuleTimeRange | null,
): SafelistingCoverageDayRow {
  const isEffective = day.status !== "cancelled" && day.status !== "skipped";
  const startM = moment(day.start_datetime).tz(tz);
  const endM = moment(day.end_datetime).tz(tz);
  const isOvernight = !endM.isSame(startM, "day");
  const startLabel = startM.format(F.full);
  const endLabel = endM.format(F.full);

  return {
    anchorDate: day.date,
    status: day.status,
    startLabel,
    endLabel,
    compactDateLabel: startM.format(F.compactDate),
    compactTimeLabel: isOvernight
      ? `${startM.format(F.time)} → ${endM.format(F.compactWithDate)}`
      : `${startM.format(F.time)} → ${endM.format(F.time)}`,
    timePatternLabel: `${startM.format(F.time)} → ${endM.format(F.time)}`,
    humanLabel:
      isEffective && rangeHours
        ? getCoverageAnchorHumanLabel(day.date, rangeHours, tz)
        : null,
  };
}

export function computeSafelistingCoverageFromDays(
  days: Safelisting["days"] | undefined,
  timeZone?: string,
  dateWord: "night" | "date" = "date",
  rangeHours?: PropertyRuleTimeRange | null,
): SafelistingCoverageFromDays | null {
  const allSorted = [...(days ?? [])].sort((a, b) => a.date.localeCompare(b.date));
  if (allSorted.length === 0) return null;

  const effectiveSorted = getSafelistingEffectiveCoverageDays(days).sort((a, b) =>
    a.date.localeCompare(b.date),
  );

  const tz = timeZone?.trim() || moment.tz.guess();

  const rows = allSorted.map((day) => mapDayToCoverageRow(day, tz, rangeHours));
  const effectiveRows = effectiveSorted.map((day) => mapDayToCoverageRow(day, tz, rangeHours));

  const cancelledCount = allSorted.filter((day) => day.status === "cancelled").length;
  const skippedCount = allSorted.filter((day) => day.status === "skipped").length;
  const permitCount = effectiveSorted.length;
  const countLabel =
    permitCount === 1 ? `1 ${dateWord}` : `${permitCount} ${dateWord}s`;

  const summaryRows = effectiveRows.length > 0 ? effectiveRows : rows;
  const first = summaryRows[0]!;
  const last = summaryRows[summaryRows.length - 1]!;

  const sharedTimePattern =
    effectiveRows.length > 1 &&
    effectiveRows.every((row) => row.timePatternLabel === effectiveRows[0]?.timePatternLabel)
      ? (effectiveRows[0]?.timePatternLabel ?? null)
      : null;

  return {
    dateWord,
    permitCount,
    countLabel,
    cancelledCount,
    skippedCount,
    overallRangeLabel: `${first.startLabel} → ${last.endLabel}`,
    overallCompactLabel: buildOverallCompactLabel(summaryRows, dateWord),
    sharedTimePattern,
    windowHint: "",
    startHumanLabel: effectiveRows[0]?.humanLabel ?? null,
    rows,
  };
}
